import { Store } from '@app/types/store';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { twMerge } from 'tailwind-merge';
import { isStoreConnected } from '../helpers';

const CDN_URL = 'https://cdn.prod.website-files.com/660e658d0cfb31720d8934d0/';

const PLATFORM_LOGOS: Record<
  string,
  {
    src: string;
    alt: string;
    containerClassName?: string;
    imgClassName?: string;
  }
> = {
  adobecommerce: {
    src: `${CDN_URL}66dfcc1fe4323db40c60b58b_adobecommerce.svg`,
    alt: 'Adobe Commerce',
    containerClassName: 'bg-[#ec2224]',
  },
  amazon: {
    src: `${CDN_URL}66dfd24e93e153ce8573fb7c_amazon.svg`,
    alt: 'Amazon',
  },
  backerkit: {
    src: `${CDN_URL}66e0d9c7c2c54f5c144f8194_backerkit.svg`,
    alt: 'BackerKit',
    containerClassName: 'bg-[#2e2eff]',
  },
  bigcommerce: {
    src: `${CDN_URL}66f51f4d0724884669141091_bigcommerce.svg`,
    alt: 'BigCommerce',
  },
  brightpearl: {
    src: `${CDN_URL}66dfcdff180eff8a50fba991_brightpearl.svg`,
    alt: 'Brightpearl',
  },
  cin7: {
    src: `${CDN_URL}66dfd292cdaf110960b4bc83_cin7.svg`,
    alt: 'Cin7',
    containerClassName: 'bg-[#0c2f6a]',
  },
  ebay: {
    src: `${CDN_URL}66dfd5c8f493a95c49c8cbc0_ebay.svg`,
    alt: 'eBay',
  },
  ecwid: {
    src: `${CDN_URL}66e0d9ffb0b9c8bb3fd755c4_ecwid.svg`,
    alt: 'Ecwid',
  },
  etsy: {
    src: `${CDN_URL}66dfd2cf1df2f05e11d496db_etsy.svg`,
    alt: 'Etsy',
    containerClassName: 'bg-[#f27224]',
  },
  groupon: {
    src: `${CDN_URL}66dfd2da7193614568154c15_groupon.svg`,
    alt: 'Groupon',
    containerClassName: 'bg-[#53a318]',
  },
  indiegogo: {
    src: `${CDN_URL}66fa8224c0c3f6d2dba9990f_indiegogo.svg`,
    alt: 'Indiegogo',
    containerClassName: 'bg-[#eb1478]',
  },
  jumia: {
    src: `${CDN_URL}66dfd2f07baeb837979ffa12_jumia.svg`,
    alt: 'Jumia',
    containerClassName: 'bg-[#f90]',
  },
  kickstarter: {
    src: `${CDN_URL}66f40df73d7eecb3f48a49dc_kickstarter.svg`,
    alt: 'Kickstarter',
    containerClassName: 'bg-[#05ce78]',
  },
  lazada: {
    src: `${CDN_URL}66dfd304f7c2d3b65d943970_lazada.svg`,
    alt: 'Lazada',
    containerClassName: 'bg-[#000083]',
  },
  linio: {
    src: `${CDN_URL}66dfd313a9d8d8fe11dad7df_linio.svg`,
    alt: 'Linio',
    containerClassName: 'bg-[#c300a2]',
  },
  linnworks: {
    src: `${CDN_URL}66dfd322f7c2d3b65d945ee3_linnworks.svg`,
    alt: 'Linnworks',
  },
  matahari: {
    src: `${CDN_URL}66dfd331d5076e9b7ad48eee_matahari.svg`,
    alt: 'Matahari',
  },
  onbuy: {
    src: `${CDN_URL}66dfd34495bb2d5e70511f7e_onbuy.svg`,
    alt: 'OnBuy',
    containerClassName: 'bg-[#3669c9]',
  },
  netsuite: {
    src: `${CDN_URL}66dfce4fa2a23d34e8bf4ed3_netsuite.svg`,
    alt: 'NetSuite',
    containerClassName: 'bg-[#29495b]',
  },
  rithum: {
    src: `${CDN_URL}66dfcf1268243f4d7a97699a_rithum.svg`,
    alt: 'Rithum',
  },
  sellbrite: {
    src: `${CDN_URL}66dfd38b0c4a5737031cf80a_sellbrite.svg`,
    alt: 'Sellbrite',
    containerClassName: 'bg-[#613ea3]',
  },
  shopify: {
    src: `${CDN_URL}66dfd372d0a21dcfcd70c4b5_shopify.svg`,
    alt: 'Shopify',
  },
  shopifyplus: {
    src: `${CDN_URL}66dfd39e9ebb73cbafa476b5_shopifyplus.svg`,
    alt: 'Shopify Plus',
  },
  shopline: {
    src: `${CDN_URL}66dfcde63570897227b4d594_shopline.svg`,
    alt: 'Shopline',
    containerClassName: 'bg-[#00142d]',
  },
  shopmatic: {
    src: `${CDN_URL}66dfd3b9d5076e9b7ad502ca_shopmatic.svg`,
    alt: 'Shopmatic',
    containerClassName: 'bg-[#2b3346]',
  },
  skuvault: {
    src: `${CDN_URL}66f51ead3c1adec2d2b1c542_skuvault.svg`,
    alt: 'Skuvault',
    containerClassName: 'bg-[#1b1c3a]',
  },
  square: {
    src: `${CDN_URL}66dfd3c693260584b1c641af_square.svg`,
    alt: 'Square',
  },
  squarespace: {
    src: `${CDN_URL}66dfd3d568243f4d7a9bc22d_squarespace.svg`,
    alt: 'Squarespace',
    imgClassName: 'h-[34px]',
  },
  tiktok: {
    src: `${CDN_URL}66dfca39f493a95c49be3c14_tiktok.svg`,
    alt: 'TikTok Shop',
  },
  walmart: {
    src: `${CDN_URL}66dfd3ef69c05af976b40eab_walmart.svg`,
    alt: 'Walmart',
    containerClassName: 'bg-[#0071dc]',
  },
  weebly: {
    src: `${CDN_URL}66dfd3fcd0a21dcfcd715d50_weebly.svg`,
    alt: 'Weebly',
    containerClassName: 'bg-[#3879fb]',
  },
  wix: {
    src: `${CDN_URL}66dfd407671227cb073db3ae_wix.svg`,
    alt: 'Wix',
  },
  woo: {
    src: `${CDN_URL}66e006984baad57688099fcb_woo.svg`,
    alt: 'Woo',
    containerClassName: 'bg-[#7f54b3]',
  },
  zalora: {
    src: `${CDN_URL}66dfd4480e13a7d31a4f3c8e_zalora.svg`,
    alt: 'Zalora',
  },
  zapier: {
    src: `${CDN_URL}66e0d9e0bb227f5ba8a971c5_zapier.svg`,
    alt: 'Zapier',
    containerClassName: 'bg-[#2b2358]',
  },
  zoho: {
    src: `${CDN_URL}66dfd456719361456816d09b_zoho.svg`,
    alt: 'Zoho',
    imgClassName: 'object-contain',
  },
};

export default function PlatformImage({ store }: { store: Store }) {
  const { platform } = store;
  const { src, alt, containerClassName, imgClassName } = PLATFORM_LOGOS[platform.cssClass];
  const isConnected = isStoreConnected(store);

  return (
    <div
      className={twMerge(
        'size-full rounded-t-xl flex flex-col gap-6 items-center justify-center bg-black',
        containerClassName
      )}
    >
      <img
        src={src}
        alt={alt}
        className={twMerge('w-[240px] max-h-[80px] object-cover', imgClassName)}
      />
      <div className="h-6 rounded-sm bg-white text-base font-bold flex gap-1 items-center justify-center p-2">
        <div
          className={twMerge(
            'size-[10px] rounded-full',
            isConnected ? 'bg-green-500' : 'bg-red-500'
          )}
        />
        <FormattedMessage
          id={`shipments.list.empty.advanced.store-${isConnected ? 'connected' : 'disconnected'}`}
        />
      </div>
    </div>
  );
}
